import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getMyTicketOrders, getOrderDetails } from '../services/eventTicketService';
import { useAuth } from '../context/AuthContext';
import './MyTickets.css';

const MyTickets = () => {
  const { isLoggedIn, user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState({});

  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login', { state: { from: '/my-tickets' } });
      return;
    }
    fetchOrders();
  }, [isLoggedIn, navigate]);

  const fetchOrders = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getMyTicketOrders();
      setOrders(data.orders || []);
    } catch (err) {
      console.error('Error fetching ticket orders:', err);
      setError('Failed to load your bookings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleExpandOrder = async (orderId) => {
    if (expandedOrder === orderId) {
      setExpandedOrder(null);
      return;
    }

    setExpandedOrder(orderId);

    if (!orderDetails[orderId]) {
      try {
        const details = await getOrderDetails(orderId);
        setOrderDetails(prev => ({ ...prev, [orderId]: details }));
      } catch (err) {
        console.error('Error fetching order details:', err);
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { class: 'bg-warning text-dark', label: 'Awaiting Approval', icon: 'bi-hourglass-split' },
      second_approval: { class: 'bg-warning text-dark', label: 'Awaiting Approval', icon: 'bi-hourglass-split' },
      approved: { class: 'bg-info text-white', label: 'Payment Pending', icon: 'bi-credit-card' },
      paid: { class: 'bg-success text-white', label: 'Confirmed', icon: 'bi-check-circle' },
      cancelled: { class: 'bg-secondary text-white', label: 'Cancelled', icon: 'bi-x-circle' },
      refunded: { class: 'bg-dark text-white', label: 'Refunded', icon: 'bi-arrow-counterclockwise' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`badge ${config.class}`}>
        <i className={`bi ${config.icon} me-1`}></i>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const formatPrice = (cents) => {
    return `EGP ${(cents / 100).toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="my-tickets-page">
        <div className="container">
          <div className="loading-state">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p>Loading your bookings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="my-tickets-page">
      <div className="container">
        <div className="page-header">
          <h1>
            <i className="bi bi-ticket-perforated"></i>
            My Bookings
          </h1>
          <p className="text-muted">View and manage your show bookings</p>
        </div>

        {error && (
          <div className="alert alert-danger">{error}</div>
        )}

        {orders.length === 0 ? (
          <div className="empty-state">
            <i className="bi bi-ticket-detailed"></i>
            <h3>No Bookings Yet</h3>
            <p>You haven't purchased any show bookings yet.</p>
            <Link to="/" className="btn btn-primary">
              Browse Shows
            </Link>
          </div>
        ) : (
          <div className="orders-list">
            {orders.map(order => (
              <div key={order.orderId} className="order-card">
                <div
                  className="order-header"
                  onClick={() => handleExpandOrder(order.orderId)}
                >
                  <div className="order-main-info">
                    <div className="order-event-info">
                      {order.eventImage && (
                        <img
                          src={order.eventImage}
                          alt={order.eventName}
                          className="event-thumbnail"
                        />
                      )}
                      <div className="event-details">
                        <h4>{order.eventName}</h4>
                        <p className="restaurant-name">
                          <i className="bi bi-geo-alt"></i>
                          {order.restaurantName}
                        </p>
                        <p className="event-datetime">
                          <i className="bi bi-calendar-event"></i>
                          {formatDate(order.eventDate)}
                          {order.eventStartTime && ` at ${formatTime(order.eventStartTime)}`}
                        </p>
                      </div>
                    </div>
                    <div className="order-meta">
                      <div className="order-id">Order #{order.orderId.slice(-8).toUpperCase()}</div>
                      {getStatusBadge(order.status)}
                    </div>
                  </div>
                  <div className="order-summary-row">
                    <span className="ticket-count">
                      <i className="bi bi-ticket"></i>
                      {order.ticketCount} booking{order.ticketCount !== 1 ? 's' : ''}
                    </span>
                    <span className="order-total">{formatPrice(order.totalCents)}</span>
                    <i className={`bi bi-chevron-${expandedOrder === order.orderId ? 'up' : 'down'} expand-icon`}></i>
                  </div>
                </div>

                {expandedOrder === order.orderId && (
                  <div className="order-details">
                    {orderDetails[order.orderId] ? (
                      <>
                        <div className="tickets-list">
                          <h5>Bookings</h5>
                          {orderDetails[order.orderId].tickets.map((ticket, idx) => (
                            <div key={ticket.id} className="ticket-item">
                              <div className="ticket-info">
                                <span className="ticket-number">#{idx + 1}</span>
                                <div className="ticket-holder">
                                  <strong>{ticket.ticketTypeName}</strong>
                                  <span className="holder-name">
                                    {ticket.firstName} {ticket.lastName}
                                  </span>
                                </div>
                              </div>
                              <div className="ticket-status">
                                {ticket.qrCodeScanned ? (
                                  <span className="badge bg-success">
                                    <i className="bi bi-check-circle me-1"></i>
                                    Scanned
                                  </span>
                                ) : order.status === 'paid' ? (
                                  <span className="badge bg-primary">
                                    <i className="bi bi-qr-code me-1"></i>
                                    Valid
                                  </span>
                                ) : (
                                  <span className="badge bg-secondary">
                                    <i className="bi bi-clock me-1"></i>
                                    Pending
                                  </span>
                                )}
                                <span className="ticket-price">
                                  {formatPrice(ticket.priceCents)}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>

                        {order.status === 'approved' && orderDetails[order.orderId].paymentUrl && (
                          <div className="payment-action">
                            <a
                              href={orderDetails[order.orderId].paymentUrl}
                              className="btn btn-success btn-lg"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <i className="bi bi-credit-card me-2"></i>
                              Pay Now
                            </a>
                            <p className="payment-note text-muted">
                              Complete your payment to receive your bookings
                            </p>
                          </div>
                        )}

                        {order.status === 'paid' && (
                          <div className="qr-info">
                            <i className="bi bi-envelope-check"></i>
                            <p>Your QR codes have been sent to your email. Present them at the show entrance.</p>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="loading-details">
                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <span>Loading details...</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyTickets;
